/**
 * Filter foods based on search term and category
 * @param {Array} foods - Array of food objects
 * @param {string} searchTerm - Search term
 * @param {string} selectedCategory - Selected category filter
 * @returns {Array} Filtered foods array
 */
export const filterFoods = (foods, searchTerm, selectedCategory) => {
  return foods.filter(food => {
    const matchesSearch = food.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'toutes' || food.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
};

/**
 * Export data as JSON file
 * @param {Array} foods - Foods data to export
 * @param {string} filename - Export filename
 */
export const exportDataAsJSON = (foods, filename = 'donnees_glycemiques.json') => {
  const dataStr = JSON.stringify(foods, null, 2);
  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
  const linkElement = document.createElement('a');
  linkElement.setAttribute('href', dataUri);
  linkElement.setAttribute('download', filename);
  linkElement.click();
};

/**
 * Generate unique ID for new foods
 * @param {Array} existingFoods - Array of existing foods
 * @returns {number} New unique ID
 */
export const generateFoodId = (existingFoods) => {
  const maxId = existingFoods.reduce((max, food) => Math.max(max, food.id || 0), 0);
  return maxId + 1;
};

/**
 * Validate food data
 * @param {Object} food - Food object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateFood = (food) => {
  const errors = [];
  
  if (!food.name || food.name.trim().length === 0) {
    errors.push('Le nom de l\'aliment est requis');
  }
  
  if (food.gi === undefined || food.gi === null || food.gi < 0 || food.gi > 100) {
    errors.push('L\'indice glycémique doit être entre 0 et 100');
  }

  if (food.carbs === undefined || food.carbs === null || food.carbs < 0) {
    errors.push('Les glucides doivent être supérieurs ou égaux à 0');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format date for display
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

/**
 * Debounce function to limit API calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if user is online
 * @returns {boolean} Online status
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Show notification to user
 * @param {string} message - Message to show
 * @param {string} type - Notification type (success, error, info)
 */
export const showNotification = (message, type = 'info') => {
  // Simple alert for now, can be replaced with toast library
  alert(message);
};

/**
 * Parse nutrition data from Open Food Facts
 * @param {Object} product - Product data from OFF
 * @returns {Object} Parsed nutrition data
 */
export const parseNutritionData = (product) => {
  const nutriments = product.nutriments || {};
  return {
    carbohydrates: nutriments.carbohydrates_100g || 0,
    sugars: nutriments.sugars_100g || 0,
    fiber: nutriments.fiber_100g || 0,
    proteins: nutriments.proteins_100g || 0,
    fat: nutriments.fat_100g || 0,
    energy: nutriments.energy_100g || 0
  };
};